/**
 * Histórico de envios de dados fitness ao prontuário (FHIR Observation via GetObservation).
 * Filtra pelos mesmos LOINCs do envio Google Health e agrupa por horário de `issued`, como WearableObservationsDialog.
 * Cards alinhados visualmente aos do FitnessDataModal (ícone + rótulo + valor).
 */

import {
  AccessTime,
  Bedtime,
  Bloodtype,
  DirectionsRun,
  Favorite,
  History,
  LocalFireDepartment,
  MonitorHeart,
  MonitorWeight,
  PersonOutline,
  Straighten,
} from "@mui/icons-material";
import type { ElementType } from "react";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import { GetObservation } from "@/services/fhir/observation/GetObservation";
import {
  WEARABLE_LOINC_LIST,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";
import {
  FITNESS_RANGE_PRESETS,
  type FitnessRangeDays,
  isValidFitnessRangeDays,
  WearableDateRangeChips,
} from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { WearablePeriodDateRow } from "@/pages/medicalcare/components/Wearable/WearablePeriodDateRow";
import {
  aggregateWearableMetricForPeriod,
  filterEntriesByDateRange,
  formatNumericForDisplay,
  formatWearablePeriodLabel,
  type FhirObservationEntry,
} from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";
import { formatWearableObservationValue } from "@/utils/wearableObservationFormat";

/**
 * Altura estável da região rolável (lista / skeleton / vazio) para evitar salto ao trocar o dia ou o estado.
 */
const LIST_REGION_SX = {
  minHeight: { xs: "min(50vh, 480px)", sm: 480 },
  maxHeight: { xs: "50vh", sm: 480 },
  overflowY: "auto" as const,
  gap: 1.5,
  /** Reserva espaço vertical mesmo com poucos cards — reduz “pulo” entre skeleton e conteúdo. */
  boxSizing: "border-box" as const,
};

/**
 * Chaves de `palette` do Theme.tsx (cores da marca) por métrica — mesma ideia do FitnessDataModal, sem hex solto.
 */
const METRIC_PALETTE_KEY: Record<WearableShareKey, keyof Theme["palette"]> = {
  steps: "secondary600",
  calories: "warning600",
  distance: "secondary800",
  heartRate: "error500",
  weight: "neutral700",
  activeMinutes: "primary600",
  glucose: "secondary700",
  bloodPressure: "neutral700",
  sleep: "primary900",
};

const METRIC_ICON_BY_KEY: Record<WearableShareKey, ElementType> = {
  steps: DirectionsRun,
  calories: LocalFireDepartment,
  distance: Straighten,
  heartRate: Favorite,
  weight: MonitorWeight,
  activeMinutes: AccessTime,
  glucose: Bloodtype,
  bloodPressure: MonitorHeart,
  sleep: Bedtime,
};

/** Cor `main` de uma entrada da palette customizada (Theme.tsx). */
function getPaletteMain(theme: Theme, key: keyof Theme["palette"]): string {
  const entry = theme.palette[key] as { main?: string } | undefined;
  return entry?.main ?? theme.palette.neutral600.main;
}

/** Códigos LOINC usados no envio a partir do Google Health (mesmo conjunto do PostWearableObservation). */
const WEARABLE_LOINC_CODES = new Set(WEARABLE_LOINC_LIST.map((d) => d.loinc));

function getPrimaryLoincCode(resource: any): string | undefined {
  const codings = resource?.code?.coding;
  if (!Array.isArray(codings)) return undefined;
  const fromLoinc = codings.find(
    (c: any) =>
      typeof c?.system === "string" &&
      (c.system.includes("loinc.org") || c.system === "http://loinc.org")
  );
  return fromLoinc?.code ?? codings[0]?.code;
}

function isWearableFitnessObservation(resource: any): boolean {
  const code = getPrimaryLoincCode(resource);
  return Boolean(code && WEARABLE_LOINC_CODES.has(String(code)));
}

/** Rótulo + ícone + cor (`palette` do Theme) alinhados ao card de métrica do modal principal. */
function getMetricPresentation(
  resource: any,
  theme: Theme
): {
  label: string;
  icon: ElementType;
  color: string;
} {
  const loinc = getPrimaryLoincCode(resource);
  const def = WEARABLE_LOINC_LIST.find((d) => d.loinc === loinc);
  const fallbackLabel =
    resource?.code?.text ||
    resource?.code?.coding?.[0]?.display ||
    "Observação";

  if (!def) {
    return {
      label: fallbackLabel,
      icon: MonitorHeart,
      color: theme.palette.neutral600.main,
    };
  }

  return {
    label: def.text,
    icon: METRIC_ICON_BY_KEY[def.key],
    color: getPaletteMain(theme, METRIC_PALETTE_KEY[def.key]),
  };
}

/**
 * Estado vazio com a mesma estrutura de antes: ícone, título, subtítulo e card de orientação.
 * Visual refinado: fundo em gradiente, círculo com sombra e card com borda lateral de destaque.
 */
function HistoryPanelEmptyState({
  variant,
  compact = false,
}: {
  variant: "no-login" | "no-data-day" | "no-issued-time";
  compact?: boolean;
}) {
  const theme = useTheme();

  const config = {
    "no-login": {
      icon: PersonOutline,
      title: "Histórico indisponível",
      subtitle:
        "É necessário estar autenticado para consultar os envios ao prontuário.",
      hint: "Faça login com sua conta Fluxmed e abra novamente esta tela.",
    },
    "no-data-day": {
      icon: History,
      title: "Nenhum envio no período",
      subtitle:
        "Não há dados fitness enviados ao prontuário para o intervalo selecionado.",
      hint:
        'Na tela «Google Health», use «Enviar ao prontuário» após buscar as métricas, ou escolha outro período nos filtros acima.',
    },
    "no-issued-time": {
      icon: History,
      title: "Registros sem horário de envio",
      subtitle:
        "Existem observações para este dia, mas falta o horário de envio (issued) para exibir o histórico.",
      hint: "Envie os dados novamente a partir da tela Google Health.",
    },
  }[variant];

  const IconCmp = config.icon;

  return (
    <Box
      sx={{
        borderRadius: compact ? 2 : 3,
        py: compact ? 1.5 : { xs: 2.5, sm: 3.5 },
        px: compact ? 1 : { xs: 1.5, sm: 2 },
      }}
    >
      <Stack spacing={compact ? 1.5 : 2.5} alignItems="center">
        <Box
          sx={{
            width: compact ? 56 : { xs: 92, sm: 100 },
            height: compact ? 56 : { xs: 92, sm: 100 },
            borderRadius: "50%",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconCmp
            sx={{
              fontSize: compact ? 28 : { xs: 42, sm: 46 },
              color: "primary.main",
            }}
          />
        </Box>

        <Stack
          spacing={compact ? 0.75 : 1.25}
          alignItems="center"
          textAlign="center"
          sx={{ maxWidth: 480, px: 0.5 }}
        >
          <Typography
            variant={compact ? "subtitle2" : "h6"}
            component="h3"
            fontWeight={700}
            color="primary800.main"
            sx={{ fontSize: compact ? "0.8rem" : { xs: "1.05rem", sm: "1.2rem" }, lineHeight: 1.3 }}
          >
            {config.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.5,
              maxWidth: 420,
              fontSize: compact ? "0.72rem" : undefined,
            }}
          >
            {config.subtitle}
          </Typography>
        </Stack>

        {!compact && (
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 540,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "primary100.main",
            borderLeft: (t) => `4px solid ${t.palette.primary.main}`,
            bgcolor: "neutral0.main",
            boxShadow: (t) =>
              `0 8px 28px ${alpha(t.palette.neutral1000.main, 0.06)}`,
          }}
        >
          <CardContent sx={{ py: 2.25, "&:last-child": { pb: 2.25 }, px: 2.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.65 }}
            >
              {config.hint}
            </Typography>
          </CardContent>
        </Card>
        )}
      </Stack>
    </Box>
  );
}

/** Grid 2 colunas — padrão mobile do histórico e dados fitness. */
const METRIC_GRID_COLS = { xs: 6, sm: 6 } as const;

/** Estilo unificado dos cards de métrica (2 por linha). */
function historyMetricCardSx(compact: boolean) {
  return {
    p: compact ? 0.85 : 1,
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: "divider",
    minHeight: compact ? 76 : 88,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    bgcolor: "background.paper",
    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
  };
}

/** Placeholder animado — mesmo layout 2 colunas dos cards de métrica. */
function HistoryPanelDataSkeleton({
  compact = false,
  listRegionSx = LIST_REGION_SX,
}: {
  compact?: boolean;
  listRegionSx?: typeof LIST_REGION_SX;
}) {
  return (
    <Stack
      sx={{
        ...listRegionSx,
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ py: 0.5 }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            animation="wave"
            width={52}
            height={30}
            sx={{ borderRadius: 1, flexShrink: 0 }}
          />
        ))}
      </Stack>
      <Grid container spacing={1}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item {...METRIC_GRID_COLS} key={`hist-skeleton-${index}`}>
            <Box sx={{ ...historyMetricCardSx(compact), minHeight: compact ? 76 : 88 }}>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width="55%"
                    height={22}
                  />
                  <Skeleton
                    variant="circular"
                    animation="wave"
                    width={34}
                    height={34}
                  />
                </Box>
                <Skeleton
                  variant="text"
                  animation="wave"
                  width="70%"
                  height={34}
                />
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

interface FitnessDataHistoryPanelProps {
  selectedDate: Dayjs;
  periodStart: Dayjs;
  periodEnd: Dayjs;
  rangeDays: FitnessRangeDays;
  today: Dayjs;
  patientUsername?: string;
  /** Sincroniza estado de carregamento com o pai (desabilita navegação de data no modal). */
  onLoadingChange: (loading: boolean) => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onPickDate: (date: Dayjs) => void;
  onRangeDaysChange: (days: FitnessRangeDays) => void;
  /** Layout enxuto para seção embutida no dashboard mobile. */
  compact?: boolean;
  /** Oculta a faixa de data quando o pai já exibe o seletor (embedded). */
  hideDateRow?: boolean;
  /** Tela cheia (ScreenMobile): lista ocupa mais altura vertical. */
  fullScreen?: boolean;
}

export function FitnessDataHistoryPanel({
  selectedDate,
  periodStart,
  periodEnd,
  rangeDays,
  today,
  patientUsername,
  onLoadingChange,
  onPrevDay,
  onNextDay,
  onPickDate,
  onRangeDaysChange,
  compact = false,
  hideDateRow = false,
  fullScreen = false,
}: FitnessDataHistoryPanelProps) {
  const [bundleLoading, setBundleLoading] = useState(false);
  const [rawEntries, setRawEntries] = useState<FhirObservationEntry[]>([]);
  const periodStartKey = periodStart.format("YYYY-MM-DD");
  const periodEndKey = periodEnd.format("YYYY-MM-DD");
  const periodLabel = formatWearablePeriodLabel(periodStart, periodEnd, rangeDays);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!patientUsername) {
        setRawEntries([]);
        return;
      }

      setBundleLoading(true);
      onLoadingChange(true);
      try {
        const bundle = await GetObservation({
          patientId: patientUsername,
          count: 500,
          startDate: periodStartKey,
          endDate: periodEndKey,
        });
        if (cancelled) return;
        const entries = (bundle?.entry ?? []) as FhirObservationEntry[];
        setRawEntries(entries);
      } catch {
        if (!cancelled) setRawEntries([]);
      } finally {
        if (!cancelled) {
          setBundleLoading(false);
          onLoadingChange(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [patientUsername, periodStartKey, periodEndKey, onLoadingChange]);

  const observationEntries = useMemo(() => {
    return rawEntries.filter((entry) => {
      const r = entry?.resource;
      if (!r || r.resourceType !== "Observation") return false;
      return isWearableFitnessObservation(r);
    });
  }, [rawEntries]);

  const periodEntries = useMemo(
    () => filterEntriesByDateRange(observationEntries, periodStart, periodEnd),
    [observationEntries, periodStart, periodEnd]
  );

  const metricSummaries = useMemo(() => {
    return WEARABLE_LOINC_LIST.map((def) => {
      const totalResult = aggregateWearableMetricForPeriod(
        def.loinc,
        periodEntries,
        rangeDays,
        periodStart,
        periodEnd,
        "sum"
      );
      const avgResult = aggregateWearableMetricForPeriod(
        def.loinc,
        periodEntries,
        rangeDays,
        periodStart,
        periodEnd,
        "average"
      );

      const toDisplay = (value: number | null) => {
        if (value === null) return "—";
        // Formata pelo LOINC (sono vira "Xh Ymin"); o recurso sintético não tem
        // unidade, então formatWearableObservationValue devolvia "-" para o sono.
        return formatNumericForDisplay(def.loinc, value, true);
      };

      // FC: somar médias diárias não faz sentido — no dia mostra o valor (Total)
      // e no período mostra somente a média.
      const isHeartRate = def.key === "heartRate";
      const showTotal = !isHeartRate || rangeDays === 1;
      const showAverage = !isHeartRate || rangeDays > 1;

      return {
        key: def.key,
        label: def.text,
        totalDisplay: toDisplay(totalResult.aggregatedNumeric),
        avgDisplay: toDisplay(avgResult.aggregatedNumeric),
        showTotal,
        showAverage,
        hasData:
          totalResult.aggregatedNumeric !== null ||
          avgResult.aggregatedNumeric !== null,
      };
    }).filter((m) => m.hasData);
  }, [periodEntries, rangeDays, periodStart, periodEnd]);

  /**
   * Visão de 1 dia: envios agrupados pelo horário de `issued` (mais recente primeiro),
   * com chips para alternar entre horários — como no histórico original.
   */
  const issuedGroups = useMemo(() => {
    if (rangeDays !== 1) return [];
    const groups = new Map<string, { ts: number; items: FhirObservationEntry[] }>();

    periodEntries.forEach((entry) => {
      const issued = entry?.resource?.issued;
      if (!issued) return;
      const parsed = dayjs(issued);
      if (!parsed.isValid()) return;

      const key = parsed.format("HH:mm");
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(entry);
        existing.ts = Math.max(existing.ts, parsed.valueOf());
      } else {
        groups.set(key, { ts: parsed.valueOf(), items: [entry] });
      }
    });

    return Array.from(groups.entries())
      .map(([time, group]) => ({ time, ...group }))
      .sort((a, b) => b.ts - a.ts);
  }, [periodEntries, rangeDays]);

  /** Horário selecionado nos chips; padrão = envio mais recente do dia. */
  const [selectedIssuedTime, setSelectedIssuedTime] = useState<string>("");

  /**
   * Reseta a seleção apenas quando a LISTA de horários muda de fato (chave estável).
   * `issuedGroups` ganha nova identidade a cada render do pai (periodStart/periodEnd
   * são recriados), o que resetava a seleção e impedia ver horários anteriores.
   */
  const issuedTimesKey = issuedGroups.map((g) => g.time).join("|");

  useEffect(() => {
    setSelectedIssuedTime((current) => {
      if (current && issuedGroups.some((g) => g.time === current)) return current;
      return issuedGroups[0]?.time ?? "";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuedTimesKey]);

  const observationsForSelectedTime = useMemo(
    () => issuedGroups.find((g) => g.time === selectedIssuedTime)?.items ?? [],
    [issuedGroups, selectedIssuedTime]
  );

  const loading = bundleLoading;
  const dateRowBusy = loading;
  const theme = useTheme();
  const listRegionSx = fullScreen
    ? {
        minHeight: { xs: "min(55vh, 520px)", sm: 480 },
        maxHeight: { xs: "calc(100dvh - 280px)", sm: 520 },
        overflowY: "auto" as const,
        gap: 1.5,
        boxSizing: "border-box" as const,
      }
    : compact
      ? {
          minHeight: 160,
          maxHeight: 220,
          overflowY: "auto" as const,
          gap: 0.75,
          boxSizing: "border-box" as const,
        }
      : LIST_REGION_SX;

  return (
    <Stack spacing={compact ? 1 : 2} sx={{ pt: hideDateRow ? 0 : 1 }}>
      {!hideDateRow && (
      <Paper
        elevation={0}
        sx={{
          p: compact ? 1 : { xs: 1.5, sm: 2 },
          backgroundColor: "primary800.main",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack spacing={compact ? 0.75 : 1.5} alignItems="center">
          <WearableDateRangeChips
            rangeDays={rangeDays}
            onChange={(days) => {
              if (isValidFitnessRangeDays(days)) onRangeDaysChange(days);
            }}
            disabled={dateRowBusy}
            tone="onPrimary"
            presets={FITNESS_RANGE_PRESETS}
          />
          <WearablePeriodDateRow
            periodLabel={periodLabel}
            periodEnd={periodEnd}
            today={today}
            loading={dateRowBusy}
            onPrevDay={onPrevDay}
            onNextDay={onNextDay}
            onPickDate={onPickDate}
          />
        </Stack>
      </Paper>
      )}

      {loading ? (
        <Box
          sx={{
            ...listRegionSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflowY: "hidden",
          }}
        >
          <CircularProgress size={compact ? 28 : 40} />
        </Box>
      ) : rangeDays === 1 ? (
        /* Visão de 1 dia: somente os envios filtrados por horário (sem resumo duplicado). */
        issuedGroups.length === 0 ? (
          <Box
            sx={{
              ...listRegionSx,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflowY: "auto",
              py: compact ? 1 : 2,
            }}
          >
            <HistoryPanelEmptyState
              variant={
                !patientUsername
                  ? "no-login"
                  : periodEntries.length > 0
                    ? "no-issued-time"
                    : "no-data-day"
              }
              compact={compact}
            />
          </Box>
        ) : (
          <Fade in appear timeout={220} key={`day_${periodEndKey}`}>
            <Stack sx={listRegionSx}>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "neutral0.main",
                  py: 0.35,
                }}
              >
                {issuedGroups.map((group) => (
                  <Button
                    key={group.time}
                    size="small"
                    variant={
                      selectedIssuedTime === group.time ? "contained" : "outlined"
                    }
                    color="primary"
                    onClick={() => setSelectedIssuedTime(group.time)}
                    sx={{
                      minHeight: compact ? 26 : undefined,
                      fontSize: compact ? "0.68rem" : undefined,
                      py: compact ? 0.15 : undefined,
                      px: compact ? 0.85 : undefined,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {group.time}
                  </Button>
                ))}
              </Stack>

              <Grid container spacing={1}>
                {observationsForSelectedTime.map((entry, index) => {
                  const resource = entry?.resource;
                  const { label, icon: MetricIcon, color } = getMetricPresentation(
                    resource,
                    theme
                  );
                  const value = formatWearableObservationValue(resource);
                  const rowKey =
                    resource?.id ??
                    `${getPrimaryLoincCode(resource) ?? "obs"}-${index}`;

                  return (
                    <Grid item {...METRIC_GRID_COLS} key={rowKey}>
                      <Box sx={historyMetricCardSx(compact)}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.6875rem",
                              lineHeight: 1.2,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              minWidth: 0,
                              flex: 1,
                              color: "text.secondary",
                            }}
                          >
                            {label}
                          </Typography>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1.5,
                              bgcolor: alpha(color, 0.12),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <MetricIcon sx={{ color, fontSize: 15 }} />
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            mt: 0.75,
                            fontWeight: 700,
                            fontSize: "0.8125rem",
                            wordBreak: "break-word",
                            lineHeight: 1.2,
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          </Fade>
        )
      ) : metricSummaries.length === 0 ? (
        <Box
          sx={{
            ...listRegionSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflowY: "auto",
            py: compact ? 1 : 2,
          }}
        >
          <HistoryPanelEmptyState
            variant={patientUsername ? "no-data-day" : "no-login"}
            compact={compact}
          />
        </Box>
      ) : (
        <Fade
          in
          appear
          timeout={220}
          key={`${periodStartKey}_${periodEndKey}_${rangeDays}`}
        >
          <Stack sx={listRegionSx}>
            <Grid container spacing={1}>
              {metricSummaries.map((metric) => {
                const def = WEARABLE_LOINC_LIST.find((d) => d.key === metric.key);
                const { label, icon: MetricIcon, color } = getMetricPresentation(
                  { code: { coding: [{ code: def?.loinc }] } },
                  theme
                );

                return (
                  <Grid item {...METRIC_GRID_COLS} key={metric.key}>
                    <Box sx={historyMetricCardSx(compact)}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.6875rem",
                            lineHeight: 1.2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minWidth: 0,
                            flex: 1,
                            color: "text.secondary",
                          }}
                        >
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            bgcolor: alpha(color, 0.12),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <MetricIcon sx={{ color, fontSize: 15 }} />
                        </Box>
                      </Box>
                      {metric.showTotal && (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            mt: 0.75,
                            fontWeight: 700,
                            fontSize: "0.8125rem",
                            wordBreak: "break-word",
                            lineHeight: 1.2,
                          }}
                        >
                          Total: {metric.totalDisplay}
                        </Typography>
                      )}
                      {metric.showAverage && (
                        <Typography
                          variant={metric.showTotal ? "caption" : "body2"}
                          color={metric.showTotal ? "text.secondary" : "text.primary"}
                          sx={{
                            mt: metric.showTotal ? 0.35 : 0.75,
                            display: "block",
                            fontWeight: metric.showTotal ? 600 : 700,
                            fontSize: metric.showTotal ? "0.72rem" : "0.8125rem",
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                          }}
                        >
                          Média: {metric.avgDisplay}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

          </Stack>
        </Fade>
      )}
    </Stack>
  );
}
